import * as React from 'react';

import { Typography2 } from '@coursera/cds-core';

import { InviteAcceptanceContainer } from 'bundles/enterprise-admin-onboarding/components/InviteAcceptance/ui';
import { useEnterpriseAdminPermissions } from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsProvider';
import { Button, Spacer } from 'bundles/enterprise-ui/components';

import _t from 'i18n!nls/admin-dashboard';

const AlertIcon = (_props: {}) => (
  <svg width="144" height="144" viewBox="0 0 144 144" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M55.9199 40.1102C37.3199 45.0902 40.9199 72.8102 34.1399 88.1102C32.1299 92.6702 29.7299 97.3202 30.0899 102.48C30.5999 109.68 36.4199 115.5 42.3899 119.19C49.4814 123.698 57.5874 126.361 65.9707 126.938C74.3539 127.514 82.7481 125.985 90.3898 122.49C96.8221 119.483 102.368 114.865 106.489 109.083C110.61 103.301 113.166 96.5515 113.91 89.4902C115.17 73.8302 106.59 58.2002 95.2799 47.3102C83.9699 36.4202 68.2799 30.4802 55.9499 40.1402"
      fill="var(--cds-color-blue-50)"
    />
    <path
      d="M40.7691 69.0303C46.4189 69.0303 50.9991 64.4502 50.9991 58.8003C50.9991 53.1504 46.4189 48.5703 40.7691 48.5703C35.1192 48.5703 30.5391 53.1504 30.5391 58.8003C30.5391 64.4502 35.1192 69.0303 40.7691 69.0303Z"
      stroke="black"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path
      d="M21.7793 96.5416C21.7809 93.1359 22.6983 89.7934 24.4355 86.8641C26.1726 83.9348 28.6656 81.5266 31.6531 79.8917C34.6407 78.2569 38.0129 77.4555 41.4166 77.5716C44.8203 77.6877 48.1301 78.7169 50.9993 80.5516"
      stroke="black"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path
      d="M103.23 69.0303C108.88 69.0303 113.46 64.4502 113.46 58.8003C113.46 53.1504 108.88 48.5703 103.23 48.5703C97.5801 48.5703 93 53.1504 93 58.8003C93 64.4502 97.5801 69.0303 103.23 69.0303Z"
      stroke="black"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path
      d="M122.22 96.5416C122.218 93.1359 121.301 89.7934 119.564 86.8641C117.827 83.9348 115.334 81.5266 112.346 79.8917C109.359 78.2569 105.986 77.4555 102.583 77.5716C99.179 77.6877 95.8692 78.7169 93 80.5516"
      stroke="black"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path
      d="M71.6395 67.8609C78.5154 67.8609 84.0895 62.2869 84.0895 55.4109C84.0895 48.535 78.5154 42.9609 71.6395 42.9609C64.7635 42.9609 59.1895 48.535 59.1895 55.4109C59.1895 62.2869 64.7635 67.8609 71.6395 67.8609Z"
      stroke="black"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path
      d="M48.2695 101.041C48.4864 94.9914 51.0419 89.2622 55.3981 85.0593C59.7544 80.8565 65.5714 78.5078 71.6245 78.5078C77.6777 78.5078 83.4947 80.8565 87.8509 85.0593C92.2071 89.2622 94.7627 94.9914 94.9795 101.041"
      stroke="black"
      strokeWidth="2"
      strokeLinejoin="round"
    />
  </svg>
);

export const YouHaveBeenGrantedAdminPrivileges = ({ onContinue }: { onContinue: () => void }) => {
  const { canViewOrg, getIsProgramDomainAdmin, hasPermission } = useEnterpriseAdminPermissions();
  if (!canViewOrg) {
    return null;
  }
  let title = _t('You have been granted administrator privileges');
  let description = _t(
    'As an organization administrator you can manage learners and administrators across your entire organization.'
  );
  if (getIsProgramDomainAdmin()) {
    title = _t('You have been granted program administrator privileges');
    description = _t('As a program administrator you can manage learners within your designated learning programs.');
  }
  if (
    hasPermission({
      domain: 'ORGANIZATION',
      subdomain: 'RECRUITMENT_PIPELINE',
      verb: 'VIEW',
    })
  ) {
    title = _t('You have been granted recruiter privileges');
    description = _t('As a recruiter you can manage the recruitment pipeline.');
  }
  return (
    <InviteAcceptanceContainer data-testid="privileges-granted">
      <Spacer size={64} />
      <AlertIcon />
      <Spacer size={32} />
      <Typography2 component="h1" variant="titleMedium">
        {title}
      </Typography2>
      <Spacer size={24} />
      <Typography2 component="p" variant="subtitleMedium">
        {description}
      </Typography2>
      <Spacer size={32} />
      <Button variant="primary" onClick={onContinue} data-e2e="continue">
        {_t('Continue to Coursera')}
      </Button>
    </InviteAcceptanceContainer>
  );
};
